/**
 * Size guide unit toggle.
 *
 * Pairs the buttons in `.sizeguidebuttons` with the `.sgt` panels inside the same
 * `.sizeguidetables` container, so every size guide on a page toggles independently.
 *
 * Expected markup (unchanged from the snippet):
 *
 *   <div class="sizeguidetables">
 *     <nav class="sizeguidebuttons">
 *       <button type="button" class="btn btn-secondary btn-xs">Inches</button>
 *       <button type="button" class="btn btn-link btn-xs">Cm</button>
 *     </nav>
 *     <div class="sgt sgt-inch active"><table>...</table></div>
 *     <div class="sgt sgt-cm"><table>...</table></div>
 *   </div>
 *
 * Buttons pair with panels by position, so a third unit works by adding a third
 * button and a third panel. To pair explicitly instead, put `data-unit="cm"` on a
 * button and it will drive the `.sgt-cm` panel regardless of order.
 *
 * Clicks are delegated from the document, so size guides rendered into a drawer or
 * modal after page load work without re-initialising anything.
 */
(function () {
  "use strict";

  const CONTAINER = ".sizeguidetables";
  const NAV = ".sizeguidebuttons";
  const PANEL = ".sgt";
  const BUTTONS = "a, .sizeguidebuttons button";

  const PANEL_ACTIVE = "active";
  const BUTTON_ACTIVE = "btn-active";
  const BUTTON_INACTIVE = "btn";
  const READY = "data-size-guide-ready";

  let uid = 0;

  /**
   * Collects a container's nav, buttons and panels.
   * Panels are filtered to this container so nested size guides cannot hijack
   * each other's tables.
   */
  function getParts(container) {
    const nav = container.querySelector(NAV);
    if (!nav) return null;

    const buttons = Array.from(nav.querySelectorAll(BUTTONS));
    const panels = Array.from(container.querySelectorAll(PANEL)).filter(
      (panel) => panel.closest(CONTAINER) === container,
    );

    if (!buttons.length || !panels.length) return null;
    return { nav, buttons, panels };
  }

  /** Resolves the panel a button controls: explicit data-unit first, then position. */
  function panelFor(button, index, panels) {
    const unit = button.dataset.unit;
    if (unit) {
      const match = panels.find((panel) =>
        panel.classList.contains(`sgt-${unit}`),
      );
      if (match) return match;
    }
    return panels[index] || null;
  }

  function select(container, index) {
    const parts = getParts(container);
    if (!parts) return;

    const { buttons, panels } = parts;
    const selected = panelFor(buttons[index], index, panels);
    if (!selected) return;

    panels.forEach((panel) => {
      panel.classList.toggle(PANEL_ACTIVE, panel === selected);
    });

    buttons.forEach((button, i) => {
      const isActive = i === index;
      button.classList.toggle(BUTTON_ACTIVE, isActive);
      button.classList.toggle(BUTTON_INACTIVE, !isActive);
      button.setAttribute("aria-selected", isActive ? "true" : "false");
      // Roving tabindex: only the selected tab is in the tab order.
      button.setAttribute("tabindex", isActive ? "0" : "-1");
    });
  }

  function init(container) {
    if (container.hasAttribute(READY)) return;

    const parts = getParts(container);
    if (!parts) return;

    container.setAttribute(READY, "");

    const { nav, buttons, panels } = parts;
    const scope = `size-guide-${++uid}`;

    nav.setAttribute("role", "tablist");

    buttons.forEach((button, i) => {
      button.setAttribute("role", "tab");

      const panel = panelFor(button, i, panels);
      if (!panel) return;

      if (!button.id) button.id = `${scope}-tab-${i}`;
      if (!panel.id) panel.id = `${scope}-panel-${i}`;

      button.setAttribute("aria-controls", panel.id);
      panel.setAttribute("role", "tabpanel");
      panel.setAttribute("aria-labelledby", button.id);
      panel.setAttribute("tabindex", "0");
    });

    // Honour whichever panel the markup marked active, so the no-JS default wins.
    let initial = panels.findIndex((panel) =>
      panel.classList.contains(PANEL_ACTIVE),
    );
    if (initial < 0) initial = 0;

    const initialButton = buttons.findIndex(
      (button, i) => panelFor(button, i, panels) === panels[initial],
    );

    select(container, initialButton < 0 ? 0 : initialButton);
  }

  function initAll(root) {
    const scope = root && root.querySelectorAll ? root : document;
    if (scope.matches && scope.matches(CONTAINER)) init(scope);
    scope.querySelectorAll(CONTAINER).forEach(init);
  }

  /** Finds the toggle button an event originated from, if any. */
  function buttonFrom(event) {
    const target = event.target;
    if (!target || !target.closest) return null;

    const button = target.closest(
      `${CONTAINER} ${NAV} a, ${CONTAINER} ${NAV} button`,
    );
    if (!button) return null;

    const container = button.closest(CONTAINER);
    if (!container) return null;

    return { button, container };
  }

  document.addEventListener("click", function (event) {
    const found = buttonFrom(event);
    if (!found) return;

    const { button, container } = found;

    // The markup uses href="#", which would otherwise jump the page to the top.
    event.preventDefault();

    // Lazily initialise, in case this guide was injected after the last init pass.
    init(container);

    const parts = getParts(container);
    if (!parts) return;

    const index = parts.buttons.indexOf(button);
    if (index >= 0) select(container, index);
  });

  // Arrow key navigation, matching the tabs pattern used elsewhere in the theme.
  document.addEventListener("keydown", function (event) {
    const keys = ["ArrowLeft", "ArrowRight", "Home", "End"];
    if (!keys.includes(event.key)) return;

    const found = buttonFrom(event);
    if (!found) return;

    const parts = getParts(found.container);
    if (!parts) return;

    const { buttons } = parts;
    const current = buttons.indexOf(found.button);
    if (current < 0) return;

    let next = current;
    if (event.key === "ArrowLeft") next = current - 1;
    if (event.key === "ArrowRight") next = current + 1;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = buttons.length - 1;

    if (next < 0 || next >= buttons.length || next === current) return;

    event.preventDefault();
    select(found.container, next);
    buttons[next].focus();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Theme editor re-renders sections without a page load.
  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });

  // Picks up size guides added to the DOM later (drawers, modals, quick view).
  if (window.MutationObserver) {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          initAll(node);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  window.SizeGuideToggle = { init: initAll };
})();
